import { useState } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Target, Users, Upload, CreditCard, Settings as SettingsIcon, LogOut, UsersRound, BarChart3, Mail, Globe, Sparkles, Zap, ShieldAlert, Kanban, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import OnboardingModal from "@/components/OnboardingModal";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/app/campaigns", icon: Target, label: "Campaigns" },
  { to: "/app/leads", icon: Users, label: "Leads" },
  { to: "/app/sequences", icon: Mail, label: "Sequences" },
  { section: "CRM" },
  { to: "/app/crm", icon: Kanban, label: "Pipeline Board" },
  { section: "Lead Generation" },
  { to: "/app/scraper", icon: Globe, label: "Domain Scraper" },
  { to: "/app/enrich", icon: Sparkles, label: "Enrich CSV" },
  { to: "/app/triggers", icon: Zap, label: "Trigger Events" },
  { to: "/app/upload", icon: Upload, label: "CSV Upload" },
  { section: "Workspace" },
  { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/app/team", icon: UsersRound, label: "Team" },
  { to: "/app/billing", icon: CreditCard, label: "Billing" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings" },
  { section: "Platform", adminOnly: true },
  { to: "/app/admin", icon: ShieldAlert, label: "Admin", adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const isAdmin = !!user?.is_platform_admin;

  const doLogout = () => { logout(); nav("/"); };
  const usedPct = Math.min(100, Math.round(((user?.leads_used || 0) / (user?.leads_quota || 1)) * 100));

  // Calculate remaining trial days
  const getTrialDaysLeft = () => {
    if (!user?.trial_ends_at) return 0;
    const end = new Date(user.trial_ends_at);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isTrial = user?.plan === "trial";
  const trialDaysLeft = getTrialDaysLeft();
  const isExpired = isTrial && new Date() > new Date(user?.trial_ends_at);
  const showPaywall = isExpired && !location.pathname.endsWith("/billing") && !location.pathname.endsWith("/settings");

  return (
    <div className="min-h-screen flex bg-[#FAFAF7] text-left">
      <aside className="w-64 shrink-0 bg-white border-r border-[#EAEAE3] h-screen sticky top-0 flex flex-col">
        <div className="p-5 border-b border-[#EAEAE3]">
          <Brand />
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((item, i) =>
            item.section ? (
              item.adminOnly && !isAdmin ? null : (
              <div key={i} className="px-3 pt-4 pb-1">
                <div className="text-[9px] font-mono uppercase tracking-widest text-[#9A9AA3]">{item.section}</div>
              </div>
              )
            ) : item.adminOnly && !isAdmin ? null : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`dash-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                  isActive ? "bg-[#0A0A0F] text-white font-medium shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F] hover:bg-[#F4F4EE]"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#EAEAE3]">
          <div className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-3 mb-3">
            <div className="label-mono text-[10px] mb-2">Quota · {user?.plan?.toUpperCase()}</div>
            <div className="text-xs text-[#5C5C66] mb-2">
              <span className="font-mono font-bold text-[#0A0A0F]">{user?.leads_used || 0}</span> / {user?.leads_quota || 50} prospects
            </div>
            <div className="h-1.5 bg-[#EAEAE3] rounded-full overflow-hidden">
              <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${usedPct}%` }} />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[#9A9AA3] truncate">{user?.email}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={doLogout} className="rounded-lg shrink-0 hover:bg-[#F4F4EE]" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>
      
      <main className="flex-1 min-w-0 flex flex-col min-h-screen">
        {user && !user.is_verified && (
          <div className="bg-[#FF5722] text-white text-xs font-mono py-2 px-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1.5">
              <ShieldAlert className="h-4 w-4" />
              <span>Please verify your email address to unlock outreach capabilities. Check your inbox for the link.</span>
            </div>
            <button
              disabled={resendLoading}
              onClick={async () => {
                if (resendLoading) return;
                setResendLoading(true);
                try {
                  await api.post("/auth/resend-verification");
                  toast.success("Verification email resent! Check your inbox.");
                } catch (e) {
                  toast.error("Failed to resend verification email");
                } finally {
                  setResendLoading(false);
                }
              }}
              className="underline hover:text-orange-200 transition-colors cursor-pointer disabled:opacity-60"
            >
              {resendLoading ? "Sending…" : "Resend Link"}
            </button>
          </div>
        )}
        {isTrial && !isExpired && (
          <div className="bg-[#002FA7] text-white text-xs font-mono py-2 px-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse" />
              <span>Free trial active — <strong>{trialDaysLeft} days remaining</strong></span>
            </div>
            <Link to="/app/billing" className="underline hover:text-blue-200 transition-colors">Upgrade Workspace →</Link>
          </div>
        )}
        
        <div className="flex-1 min-h-0 overflow-y-auto">
          {showPaywall ? (
            <div className="min-h-[calc(100vh-40px)] flex items-center justify-center p-6 bg-[#FAFAF7]">
              <div className="max-w-md w-full bg-white border border-[#EAEAE3] rounded-2xl p-8 shadow-soft-lg text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                  <ShieldAlert className="h-8 w-8 text-[#FF5722]" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-display text-2xl font-black tracking-tight text-[#0A0A0F]">Your free trial has ended</h2>
                  <p className="text-sm text-[#5C5C66] leading-relaxed">
                    Your 7-day free trial of LeadsB2B has expired. Upgrade your workspace to a paid plan to keep generating leads and running outreach.
                  </p>
                </div>
                <Button onClick={() => nav("/app/billing")} className="w-full rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white h-11">
                  View Pricing & Upgrade <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Outlet />
              <OnboardingModal />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
